import { FC, useMemo } from 'react'
import { MixedAccountsTableItem } from 'selectors/linkageSelectors'
import { LinkedAccountLayout } from 'components/features/pfm/LinkedAccountLayout/LinkedAccountLayout'
import { LinkedAccountName } from 'components/features/pfm/LinkedAccounts/LinkedAccountName'
import { ProviderAccountState } from 'interfaces/linkage'
import styles from 'components/features/pfm/LinkedAccounts/LinkedAccount.styl'
import { AccountActionsProps, AccountActions } from 'components/features/pfm/AccountActions/AccountActions'
import { LinkedAccountBalance } from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalance'
import { isInProgressState } from 'helpers/linkageHelpers'
import { LinkedAccountProvider } from 'components/features/pfm/LinkedAccountProvider/LinkedAccountProvider'
import { SortParam } from 'components/features/pfm/LinkedAccounts/OrderBy'
import { LinkedAccountCategory } from 'components/features/pfm/LinkedAccountCategory/LinkedAccountCategory'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'

type LinkedAccountProps =
  & Pick<AccountActionsProps, 'menuDirection'>
  & {
    accountInfo: MixedAccountsTableItem
    isFirst: boolean
    inSpan: number
    sortBy: SortParam
    groupSum: number
  }

export const LinkedAccount: FC<LinkedAccountProps> = (props) => {
  const { accountInfo, isFirst, inSpan, menuDirection, sortBy, groupSum } = props
  const { account, providerAccountState } = accountInfo
  const { status } = providerAccountState
  const { link_status = null } = account || {}

  const isUnlinked = link_status === 'unlinked'


  const accountChild = useMemo(() => {
    if (account) {
      return (
        <LinkedAccountName account={ account } />
      )
    }

    const isInProgress = isInProgressState(providerAccountState as ProviderAccountState)
    if (isInProgress) {
      return (
        <span className={ styles.shy }>
          Linking in progress...
        </span>
      )
    }

    if (status === 'ready') {
      return (
        <span className={ styles.shy }>
          No account.
        </span>
      )
    }

    return null
  }, [status, account, providerAccountState])

  const isMobile = useStoreSelector(isMobileSelector)

  const balanceChild = useMemo(() => (
    <LinkedAccountBalance accountInfo={ accountInfo } />
  ), [accountInfo])

  const categoryChild = useMemo(() => (
    <LinkedAccountCategory accountInfo={ accountInfo } hideIcon={ isMobile && sortBy !== 'categoryName' } />
  ), [accountInfo, sortBy, isMobile])

  const providerChild = useMemo(() => (
    <LinkedAccountProvider accountInfo={ accountInfo } hideIcon={ isMobile && sortBy === 'categoryName' } />
  ), [accountInfo, sortBy, isMobile])

  const actionsChild = useMemo(() => (
    <AccountActions accountInfo={ accountInfo } menuDirection={ menuDirection } />
  ), [accountInfo, menuDirection])

  const groupSumChild = useMemo(() => {
    return (
      <div className={ styles.groupSum }>
        { formatUSCurrency(groupSum) }
      </div>
    )
  }, [groupSum])

  return (
    <LinkedAccountLayout
      isFirst={ isFirst }
      inSpan={ inSpan }
      mode={ sortBy === 'categoryName' ? 'byCategory' : 'byProvider' }
      isUnlinked={ isUnlinked }

      provider={ providerChild }
      account={ accountChild }
      balance={ balanceChild }
      category={ categoryChild }
      actions={ actionsChild }
      sum={ groupSumChild }
    />
  )
}
